import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import { Caption } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import ms from 'ms';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import BidConsoleOpener from '@/components/BidConsoleOpener/BidConsoleOpener';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import WhyCantRetractBidPopover from '@/components/Popovers/WhyCantRetractBidPopover';
import RegisterLink from '@/components/RegisterLink/RegisterLink';
import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getBidderId, getUserIsInGoodStanding } from '@/redux/modules/account/user/user.selectors';
import { EventContext } from '@/redux/modules/analytics';
import { getBidLimitBalance } from '@/redux/modules/bidLimit/bidLimit.selectors';
import { getCatalogIsInstantAutopay } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getCatalogNetworkStatus,
    getLatestMessageTimestamp,
    getLiveCatalogClosed,
    getLiveCatalogCurrentItemId,
    getLiveCatalogStatus,
    getLiveCatalogWon,
    getLiveItemDataCurrentAskPrice,
    getLiveItemDataLeadingBidder,
} from '@/redux/modules/console';
import {
    getItemSummaryBidderMaxBid,
    getItemSummaryCatalogId,
    getItemSummaryCatalogStatus,
    getItemSummaryCurrency,
    getItemSummaryIsCatalogOnly,
    getItemSummaryLeadingBid,
    getItemSummarySaleStartTs,
    getItemSummarySellerId,
    getItemSummarySellerName,
    getItemSummaryStartPrice,
    getItemSummaryUrl,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getUiLiveBid, submitLiveBid } from '@/redux/modules/liveBid';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import {
    CompleteAccountModalContext,
    ModalContext,
    openPlaceAbsenteeBidModal,
    openRetractBidModal,
    openSendSellerMessageModal,
} from '@/redux/modules/modal';
import { AuctionStatus } from '@/types/LiveAuctionState';
import { shouldShowLive } from '@/utils/live';

import CardCtaBanner from './CardCtaBanner';

type Props = {
    allowBidding?: boolean;
    itemId: number;
    showBanner?: boolean;
    showCta?: boolean;
};

const CardCta = (props: Props) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const { allowBidding, itemId, showBanner, showCta } = props;
    const bidderId = useAppSelector(getBidderId);
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));
    const currentAskPrice = useAppSelector((state) => getLiveItemDataCurrentAskPrice(state, itemId));
    const leadingBidderId = useAppSelector((state) => getLiveItemDataLeadingBidder(state, itemId));
    const location = useLocation();

    const accountInGoodStanding = useAppSelector(getUserIsInGoodStanding);

    const bidderMaxBid = useAppSelector((state) => getItemSummaryBidderMaxBid(state, itemId));
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const catalogTitle = useAppSelector((state) => getItemSummaryCurrency(state, itemId));
    const currency = useAppSelector((state) => getItemSummaryCurrency(state, itemId));
    const isCatalogOnly = useAppSelector((state) => getItemSummaryIsCatalogOnly(state, itemId));
    const itemUrl = useAppSelector((state) => getItemSummaryUrl(state, itemId));
    const leadingBid = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId));
    const salePrice = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId));
    const saleStartTs = useAppSelector((state) => getItemSummarySaleStartTs(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const sellerName = useAppSelector((state) => getItemSummarySellerName(state, itemId));
    const startPrice = useAppSelector((state) => getItemSummaryStartPrice(state, itemId));

    const bidLimitBalance = useAppSelector((state) => getBidLimitBalance(state, catalogId));
    const closed = useAppSelector((state) => getLiveCatalogClosed(state, catalogId));
    const currentItem = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const networkStatus = useAppSelector((state) => getCatalogNetworkStatus(state, catalogId));
    const status = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const won = useAppSelector((state) => getLiveCatalogWon(state, catalogId));
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));
    const liveUpdateTimestamp = useAppSelector((state) => getLatestMessageTimestamp(state, catalogId));
    const leadingBidder = leadingBidderId === bidderId;

    const [, setTime] = React.useState(0);

    React.useEffect(() => {
        const interval = setInterval(() => setTime(Date.now()), 5000);
        return () => {
            clearInterval(interval);
        };
    }, []);

    const { approved, bidLimit, blockedByAuctioneer, declined, limited, registered, suspended } = useAppSelector(
        (state) => getCatalogRegistration(state, catalogId)
    );

    const { submitted } = useAppSelector(getUiLiveBid);
    const currentPrice = leadingBid || salePrice || startPrice || 0;
    const overBidLimit = limited ? bidLimitBalance + currentPrice > bidLimit : false;

    const showSendSellerMessageModal = React.useCallback(() => {
        const messageDetails = {};
        const subject = `${formatMessage({
            id: 'registration_declined',
        })}: ${catalogTitle}`;

        dispatch(openSendSellerMessageModal(messageDetails, sellerId, sellerName, subject));
    }, [catalogTitle, dispatch, formatMessage, sellerId, sellerName]);

    const checkReadyToBid = React.useCallback(() => {
        const timeSinceLastMessage = Date.now() - liveUpdateTimestamp;

        return (
            itemId === currentItem &&
            itemId > 0 &&
            networkStatus === 'connected' &&
            status === 'live' &&
            !closed &&
            !won &&
            Boolean(currentAskPrice) &&
            catalogId > 0 &&
            timeSinceLastMessage < ms('5m')
        );
    }, [catalogId, currentAskPrice, itemId, closed, currentItem, networkStatus, status, won, liveUpdateTimestamp]);

    const modalContext = React.useMemo<ModalContext>(
        () => ({
            eventContext: EventContext.placeBid,
            id: itemId,
            itemId,
            type: CompleteAccountModalContext.PlaceBid,
        }),
        [itemId]
    );

    const isStoppedFromBidding =
        !accountInGoodStanding ||
        (!approved && !limited) ||
        suspended ||
        declined ||
        blockedByAuctioneer ||
        overBidLimit ||
        !checkReadyToBid();

    const showLiveButton = shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);
    const linkAndButton = React.useCallback(() => {
        const increaseText = (
            <StyledIncreaseText>
                <FormattedMessage
                    id="increase_max_of"
                    tagName="span"
                    values={{
                        bidAmount: (
                            <FormattedCurrency
                                key="increase-max-of-bid-amount"
                                noFraction
                                sourceCurrency={currency}
                                value={bidderMaxBid}
                            />
                        ),
                    }}
                />
                <Link
                    onClick={() => {
                        dispatch(
                            openPlaceAbsenteeBidModal({
                                bidAmount: 0,
                                isInstantAutopay,
                                itemId,
                                modalContext,
                            })
                        );
                    }}
                >
                    <FormattedMessage id="increase" />
                </Link>
            </StyledIncreaseText>
        );

        let link: React.ReactNode | '';
        if (suspended || !accountInGoodStanding) {
            link = (
                <StyledLink to="https://help.liveauctioneers.com/article/146-how-do-i-restore-my-suspended-account">
                    <FormattedMessage id="suspendedBidderBanner.button" />
                </StyledLink>
            );
        } else if (blockedByAuctioneer || declined) {
            link = (
                <ShouldLogInContainer
                    requireCompleteProfile
                    requireLogin
                    runFunction={showSendSellerMessageModal}
                >
                    <Link>
                        <FormattedMessage id="contact_auctioneer" />
                    </Link>
                </ShouldLogInContainer>
            );
        } else if (catalogStatus === AuctionStatus.Done) {
            link = (
                <Link
                    route
                    to={itemUrl}
                >
                    <FormattedMessage id="view_similar_items" />
                </Link>
            );
        } else if (showLiveButton) {
            link = (
                <>
                    <BidConsoleOpener catalogId={catalogId}>
                        <StyledLiveLink>
                            <FormattedMessage id="enter_live_auction" />
                        </StyledLiveLink>
                    </BidConsoleOpener>
                    {!isLotOpen && increaseText}
                </>
            );
        } else {
            link = increaseText;
        }

        let retractButton: React.ReactNode | '';
        if (catalogStatus === 'online') {
            retractButton = (
                <StyledSpanRetract
                    onClick={() => {
                        dispatch(openRetractBidModal(itemId));
                    }}
                >
                    <FormattedMessage id="retract" />
                </StyledSpanRetract>
            );
        } else if (catalogStatus === 'live') {
            retractButton = (
                <StyledSpanRetract>
                    <WhyCantRetractBidPopover />
                </StyledSpanRetract>
            );
        } else {
            retractButton = <StyledSpanRetract />;
        }
        return (
            <>
                {link}
                {retractButton}
            </>
        );
    }, [
        currency,
        bidderMaxBid,
        suspended,
        accountInGoodStanding,
        blockedByAuctioneer,
        declined,
        catalogStatus,
        showLiveButton,
        dispatch,
        isInstantAutopay,
        itemId,
        modalContext,
        showSendSellerMessageModal,
        itemUrl,
        catalogId,
        isLotOpen,
    ]);

    const placeBid = React.useCallback(() => {
        // dont only rely on the "disabled" button to prevent bidders who havent been approved
        if (accountInGoodStanding && (approved || limited) && checkReadyToBid()) {
            dispatch(submitLiveBid(Date.now(), catalogId, currentAskPrice, currency, itemId, location));
        } else {
            return false;
        }
    }, [
        accountInGoodStanding,
        approved,
        catalogId,
        checkReadyToBid,
        currency,
        currentAskPrice,
        dispatch,
        itemId,
        limited,
        location,
    ]);

    return isLotOpen && allowBidding ? (
        <StyledItemCardControl>
            {!isStoppedFromBidding && registered && !leadingBidder && (
                <Button
                    disabled={submitted}
                    loading={submitted}
                    onClick={placeBid}
                    primary
                    red
                    small
                >
                    <FormattedMessage
                        id="bidding_console.console.button.bid"
                        values={{
                            amount: (
                                <StyledBidButtonCurrency
                                    noFraction
                                    sourceCurrency={currency}
                                    value={currentAskPrice}
                                />
                            ),
                        }}
                    />
                </Button>
            )}
            {leadingBidder && (
                <StyledWinningButton
                    primary
                    small
                >
                    <FormattedMessage id="winning" />
                </StyledWinningButton>
            )}
            {!registered && <StyledRegisterLink catalogId={catalogId} />}
        </StyledItemCardControl>
    ) : showCta ? (
        <StyledItemCardControl>{linkAndButton()}</StyledItemCardControl>
    ) : showBanner && catalogStatus !== AuctionStatus.Done ? (
        <StyledItemCardControl>
            <CardCtaBanner
                catalogId={catalogId}
                itemId={itemId}
            />
        </StyledItemCardControl>
    ) : null;
};

export default React.memo<Props>(CardCta);

const StyledIncreaseText = styled.span`
    display: block;
    margin-bottom: 13px;

    & span {
        display: inline;
        margin-right: 5px;
    }

    & div {
        display: inline;
    }

    & a {
        display: inline;
        text-decoration: underline;
    }
`;

const StyledLink = styled(Link)`
    width: 100%;

    @media (max-width: 469px) {
        padding: 13px 25px;
    }
`;

const StyledLiveLink = styled(Link)`
    color: ${({ theme }) => theme.colors.red100};
`;

const StyledSpanRetract = styled(Caption)`
    flex: 1 0 25%;
    color: ${({ theme }) => theme.colors.grey100};
    cursor: pointer;
    text-decoration: none;
    max-width: 40px;
    margin-right: auto;

    & > a {
        color: ${({ theme }) => theme.colors.grey100};
        text-decoration: none;

        &:focus {
            outline: 0;
        }

        &:hover {
            text-decoration: none;
        }
    }

    &:focus {
        outline: 0;
    }

    &[disabled] {
        visibility: hidden;
    }

    @media (max-width: 600px) {
        text-align: center;
        width: 100%;
        flex: 1 0 29px;
    }
`;

const StyledItemCardControl = styled.div`
    & > a {
        flex: 1 1 75%;
        margin-right: 10px;
        display: flex;

        &.live {
            color: ${({ theme }) => theme.colors.red100};
        }

        @media (max-width: 600px) {
            flex: 1 0 auto;
            width: 100%;
        }
    }

    button {
        width: 100%;

        @media (max-width: 500px) {
            display: none;
        }
    }
`;

const StyledBidButtonCurrency = styled(FormattedCurrency)`
    margin-left: 5px;
`;

const StyledWinningButton = styled(Button)`
    background-color: ${({ theme }) => theme.colors.teal} !important;
    border-color: ${({ theme }) => theme.colors.teal} !important;
    cursor: default;
`;

const StyledRegisterLink = styled(RegisterLink)`
    text-align: center;
    display: block;
`;
